// Company license detail — one held company_licenses row with its qualifying
// agent, branch children, and the insurance policies / surety bonds its
// validity depends on (the M:N dependency graph the expiry-alert job walks).
// Link / unlink controls attach policies + bonds; an edit modal patches the
// core fields. Document upload to the vault is out of scope here (future).
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CompanyLicense,
  CompanyLicenseStatus,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DocumentAttachment } from '../components/DocumentAttachment';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Textarea,
} from '../components/ui';
import { ApiError } from '../api/client';
import {
  useCompanyLicense,
  useCompanyLicenses,
  useQualifyingAgents,
  useUpdateCompanyLicense,
  useInsurancePolicies,
  useBonds,
  useLinkLicensePolicy,
  useUnlinkLicensePolicy,
  useLinkLicenseBond,
  useUnlinkLicenseBond,
} from '../api/queries/compliance';

// ---------- Static option lists ----------

const LICENSE_STATUSES: { value: CompanyLicenseStatus; label: string }[] = [
  { value: 'applied', label: 'Applied' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'expired_grace', label: 'In grace period' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'surrendered', label: 'Surrendered' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null | undefined): string {
  return epochMs === null || epochMs === undefined
    ? '—'
    : new Date(epochMs).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
}

function licenseStatusTone(status: CompanyLicenseStatus): string {
  switch (status) {
    case 'active':
      return 'badge-success';
    case 'applied':
    case 'pending':
    case 'expired_grace':
      return 'badge-warn';
    default:
      return 'badge-muted';
  }
}

function licenseStatusLabel(status: CompanyLicenseStatus): string {
  return LICENSE_STATUSES.find((s) => s.value === status)?.label ?? status;
}

// =============================================================================
// Page
// =============================================================================

export function CompanyLicenseDetailPage(): JSX.Element {
  const { id: rawId } = useParams<{ id: string }>();
  const id = Number(rawId);
  const navigate = useNavigate();
  const canWrite = can('compliance.write');

  const licenseQuery = useCompanyLicense(Number.isInteger(id) && id > 0 ? id : null);
  const [showEdit, setShowEdit] = useState(false);

  if (licenseQuery.isPending) {
    return <p className="muted">Loading license…</p>;
  }
  if (licenseQuery.error) {
    const status = licenseQuery.error instanceof ApiError ? licenseQuery.error.status : 0;
    const msg =
      status === 404 ? 'License not found.'
        : status === 403 ? 'You do not have permission to view this license.'
          : 'Failed to load the license.';
    return (
      <section className="compliance-page">
        <p className="error-text" role="alert">{msg}</p>
        <Link className="back-link" to="/compliance/licenses">← Back to licenses</Link>
      </section>
    );
  }

  const license = licenseQuery.data.license;

  return (
    <section className="compliance-page">
      <Link className="back-link" to="/compliance/licenses">← Company licenses</Link>

      <header className="page-header detail-head">
        <div>
          <div className="detail-eyebrow">
            {license.license_type_display ?? license.license_type_code ?? `Type #${license.license_type_id}`}
          </div>
          <h2>{license.license_number ?? '(no license number)'}</h2>
          <div className="detail-badges">
            <Badge className={licenseStatusTone(license.status)}>
              {licenseStatusLabel(license.status)}
            </Badge>
            {license.armed_endorsement && <Badge className="badge-warn">Armed endorsement</Badge>}
            {license.parent_license_id !== null && <Badge className="badge-muted">Branch</Badge>}
          </div>
        </div>
        {canWrite && (
          <Button variant="ghost" onClick={() => setShowEdit(true)}>
            Edit
          </Button>
        )}
      </header>

      <dl className="facts-grid">
        <div><dt>Jurisdiction</dt><dd>{license.jurisdiction_name ?? license.jurisdiction_code ?? '—'}</dd></div>
        <div><dt>Legal entity</dt><dd>{license.legal_entity_name ?? '—'}</dd></div>
        <div><dt>DBA</dt><dd>{license.dba ?? '—'}</dd></div>
        <div><dt>Issued</dt><dd>{fmtDate(license.issue_date)}</dd></div>
        <div><dt>Expires</dt><dd>{fmtDate(license.expiration_date)}</dd></div>
        <div><dt>Categories</dt><dd>{license.categories && license.categories.length > 0 ? license.categories.join(', ') : '—'}</dd></div>
        <div><dt>Foreign qualification ref</dt><dd>{license.foreign_qualification_ref ?? '—'}</dd></div>
        <div>
          <dt>Verification</dt>
          <dd>
            {license.verification_url
              ? <a href={license.verification_url} target="_blank" rel="noreferrer">Lookup ↗</a>
              : '—'}
          </dd>
        </div>
        <div><dt>Last verified</dt><dd>{fmtDate(license.last_verified_at)}</dd></div>
        <div>
          <dt>Document</dt>
          <dd>
            <DocumentAttachment segment="licenses" id={license.id} documentPath={license.document_path} />
          </dd>
        </div>
      </dl>

      {license.notes && (
        <div className="notes-block">
          <div className="notes-label">Notes</div>
          <p>{license.notes}</p>
        </div>
      )}

      <RenewalCard license={license} />
      <QualifyingAgentCard license={license} />
      <BranchChildrenCard license={license} />
      <LinkedPoliciesCard license={license} />
      <LinkedBondsCard license={license} />

      {showEdit && (
        <EditLicenseModal license={license} onClose={() => setShowEdit(false)} />
      )}
    </section>
  );
}

// =============================================================================
// Renewal info card
// =============================================================================

function RenewalCard({ license }: { license: CompanyLicense }): JSX.Element {
  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Renewal</h3></header>
      <dl className="facts-grid">
        <div><dt>Renewal due</dt><dd>{fmtDate(license.renewal_due_date)}</dd></div>
        <div><dt>Expiration</dt><dd>{fmtDate(license.expiration_date)}</dd></div>
      </dl>
      <p className="hint">
        The renewal window and grace period come from the license type catalog;
        the compliance calendar surfaces the upcoming renewal deadline.
      </p>
    </section>
  );
}

// =============================================================================
// Qualifying agent card
// =============================================================================

function QualifyingAgentCard({ license }: { license: CompanyLicense }): JSX.Element {
  // The license row carries the QA display name but not the linked persons.id,
  // so resolve the QA record to build the /persons/:person_id link.
  const qaQuery = useQualifyingAgents();
  const qa = (qaQuery.data?.qualifying_agents ?? []).find(
    (a) => a.id === license.qualifying_agent_id,
  );
  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Qualifying agent</h3></header>
      {license.qualifying_agent_id === null ? (
        <p className="muted empty-state">No qualifying agent assigned.</p>
      ) : (
        <dl className="facts-grid">
          <div>
            <dt>Agent</dt>
            <dd>
              {qa
                ? <Link to={`/persons/${qa.person_id}`}>{qa.full_name ?? `Person #${qa.person_id}`}</Link>
                : (license.qualifying_agent_name ?? `QA #${license.qualifying_agent_id}`)}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

// =============================================================================
// Branch children table
// =============================================================================

function BranchChildrenCard({ license }: { license: CompanyLicense }): JSX.Element {
  const navigate = useNavigate();
  // Branch licenses point at their parent via parent_license_id; pull the full
  // list and filter to this license's children.
  const listQuery = useCompanyLicenses();
  const children = (listQuery.data?.licenses ?? []).filter(
    (l) => l.parent_license_id === license.id,
  );

  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Branch licenses</h3></header>
      {listQuery.isPending ? (
        <p className="muted">Loading…</p>
      ) : children.length === 0 ? (
        <p className="muted empty-state">No branch licenses under this license.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>License #</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {children.map((c) => (
              <Table.Row
                key={c.id}
                interactive
                onClick={() => navigate(`/compliance/licenses/${c.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/compliance/licenses/${c.id}`); }}
              >
                <Table.Cell tone="mono">{c.license_number ? <code>{c.license_number}</code> : '—'}</Table.Cell>
                <Table.Cell tone="strong">{c.license_type_display ?? c.license_type_code ?? `Type #${c.license_type_id}`}</Table.Cell>
                <Table.Cell>{c.jurisdiction_name ?? c.jurisdiction_code ?? '—'}</Table.Cell>
                <Table.Cell>
                  <Badge className={licenseStatusTone(c.status)}>
                    {licenseStatusLabel(c.status)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{fmtDate(c.expiration_date)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
}

// =============================================================================
// Linked insurance policies (M:N) with link / unlink controls
// =============================================================================

function LinkedPoliciesCard({ license }: { license: CompanyLicense }): JSX.Element {
  const canWrite = can('compliance.write');
  const policiesQuery = useInsurancePolicies();
  const link = useLinkLicensePolicy();
  const unlink = useUnlinkLicensePolicy();

  const [selected, setSelected] = useState('');

  const policies = policiesQuery.data?.insurance_policies ?? [];
  // The detail response may project the linked-policy ids (the M:N join the
  // expiry-alert job walks). The base CompanyLicense type doesn't declare them,
  // so read them through a tolerant optional shape.
  const linkedIds: number[] =
    ((license as CompanyLicense & { policies?: { id: number }[] }).policies ?? []).map((p) => p.id);
  const linked = policies.filter((p) => linkedIds.includes(p.id));
  const available = policies.filter((p) => !linkedIds.includes(p.id));

  const onLink = async (): Promise<void> => {
    const pid = Number(selected);
    if (!Number.isInteger(pid) || pid <= 0) return;
    try {
      await link.mutateAsync({ companyLicenseId: license.id, insurancePolicyId: pid });
      toast.success('Policy linked.');
      setSelected('');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not link the policy.'));
    }
  };

  const onUnlink = async (pid: number): Promise<void> => {
    try {
      await unlink.mutateAsync({ companyLicenseId: license.id, insurancePolicyId: pid });
      toast.success('Policy unlinked.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not unlink the policy.'));
    }
  };

  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Linked insurance policies</h3></header>
      {linked.length === 0 ? (
        <p className="muted empty-state">No insurance policies linked.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Policy #</Table.HeaderCell>
              <Table.HeaderCell>Line</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {linked.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell tone="strong">{p.carrier}</Table.Cell>
                <Table.Cell tone="mono">{p.policy_number ? <code>{p.policy_number}</code> : '—'}</Table.Cell>
                <Table.Cell>{p.line.toUpperCase()}</Table.Cell>
                <Table.Cell>{fmtDate(p.expiration_date)}</Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button
                      variant="ghost"
                      className="btn-sm"
                      onClick={() => onUnlink(p.id)}
                      disabled={unlink.isPending}
                    >
                      Unlink
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {canWrite && (
        <div className="card-toolbar">
          <select
            className="form-input"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={link.isPending || available.length === 0}
          >
            <option value="">Select a policy to link…</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.carrier} — {p.line.toUpperCase()}{p.policy_number ? ` (${p.policy_number})` : ''}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={onLink}
            disabled={link.isPending || selected === ''}
          >
            Link policy
          </Button>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// Linked surety bonds (M:N) with link / unlink controls
// =============================================================================

function LinkedBondsCard({ license }: { license: CompanyLicense }): JSX.Element {
  const canWrite = can('compliance.write');
  const bondsQuery = useBonds();
  const link = useLinkLicenseBond();
  const unlink = useUnlinkLicenseBond();

  const [selected, setSelected] = useState('');

  const bonds = bondsQuery.data?.bonds ?? [];
  const linkedIds: number[] =
    ((license as CompanyLicense & { bonds?: { id: number }[] }).bonds ?? []).map((b) => b.id);
  const linked = bonds.filter((b) => linkedIds.includes(b.id));
  const available = bonds.filter((b) => !linkedIds.includes(b.id));

  const onLink = async (): Promise<void> => {
    const bid = Number(selected);
    if (!Number.isInteger(bid) || bid <= 0) return;
    try {
      await link.mutateAsync({ companyLicenseId: license.id, suretyBondId: bid });
      toast.success('Bond linked.');
      setSelected('');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not link the bond.'));
    }
  };

  const onUnlink = async (bid: number): Promise<void> => {
    try {
      await unlink.mutateAsync({ companyLicenseId: license.id, suretyBondId: bid });
      toast.success('Bond unlinked.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not unlink the bond.'));
    }
  };

  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Linked surety bonds</h3></header>
      {linked.length === 0 ? (
        <p className="muted empty-state">No surety bonds linked.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Bond #</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {linked.map((b) => (
              <Table.Row key={b.id}>
                <Table.Cell tone="strong">{b.surety_carrier}</Table.Cell>
                <Table.Cell tone="mono">{b.bond_number ? <code>{b.bond_number}</code> : '—'}</Table.Cell>
                <Table.Cell>{b.amount ?? '—'}</Table.Cell>
                <Table.Cell>{fmtDate(b.expiration_date)}</Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button
                      variant="ghost"
                      className="btn-sm"
                      onClick={() => onUnlink(b.id)}
                      disabled={unlink.isPending}
                    >
                      Unlink
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {canWrite && (
        <div className="card-toolbar">
          <select
            className="form-input"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={link.isPending || available.length === 0}
          >
            <option value="">Select a bond to link…</option>
            {available.map((b) => (
              <option key={b.id} value={b.id}>
                {b.surety_carrier}{b.bond_number ? ` (${b.bond_number})` : ''}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={onLink}
            disabled={link.isPending || selected === ''}
          >
            Link bond
          </Button>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// Edit license modal
// =============================================================================

function EditLicenseModal({
  license,
  onClose,
}: {
  license: CompanyLicense;
  onClose: () => void;
}): JSX.Element {
  const update = useUpdateCompanyLicense();
  const busy = update.isPending;

  const [licenseNumber, setLicenseNumber] = useState(license.license_number ?? '');
  const [legalEntity, setLegalEntity] = useState(license.legal_entity_name ?? '');
  const [dba, setDba] = useState(license.dba ?? '');
  const [status, setStatus] = useState<CompanyLicenseStatus>(license.status);
  const [issueDate, setIssueDate] = useState<number | null>(license.issue_date ?? null);
  const [expirationDate, setExpirationDate] = useState<number | null>(license.expiration_date ?? null);
  const [renewalDue, setRenewalDue] = useState<number | null>(license.renewal_due_date ?? null);
  const [armed, setArmed] = useState<boolean>(license.armed_endorsement);
  const [verificationUrl, setVerificationUrl] = useState(license.verification_url ?? '');
  const [notes, setNotes] = useState(license.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        id: license.id,
        license_number: licenseNumber.trim() ? licenseNumber.trim() : null,
        legal_entity_name: legalEntity.trim() ? legalEntity.trim() : null,
        dba: dba.trim() ? dba.trim() : null,
        status,
        issue_date: issueDate,
        expiration_date: expirationDate,
        renewal_due_date: renewalDue,
        armed_endorsement: armed,
        verification_url: verificationUrl.trim() ? verificationUrl.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
      });
      toast.success('License updated.');
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save changes.'));
    }
  };

  return (
    <Dialog
      title="Edit license"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="edit-license-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="edit-license-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="License number">
            <Input
              type="text"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              maxLength={120}
              disabled={busy}
              autoFocus
            />
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as CompanyLicenseStatus)}
              disabled={busy}
            >
              {LICENSE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Legal entity name">
            <Input
              type="text"
              value={legalEntity}
              onChange={(e) => setLegalEntity(e.target.value)}
              maxLength={300}
              disabled={busy}
            />
          </FormField>
          <FormField label="DBA">
            <Input
              type="text"
              value={dba}
              onChange={(e) => setDba(e.target.value)}
              maxLength={300}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Issue date">
            <DateInput value={issueDate} onChange={setIssueDate} disabled={busy} />
          </FormField>
          <FormField label="Expiration date">
            <DateInput value={expirationDate} onChange={setExpirationDate} disabled={busy} />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Renewal due">
            <DateInput value={renewalDue} onChange={setRenewalDue} disabled={busy} />
          </FormField>
          <FormField label="Verification URL">
            <Input
              type="url"
              value={verificationUrl}
              onChange={(e) => setVerificationUrl(e.target.value)}
              maxLength={2000}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <label className="form-field checkbox-field">
          <input
            type="checkbox"
            checked={armed}
            onChange={(e) => setArmed(e.target.checked)}
            disabled={busy}
          />
          <span>Armed endorsement</span>
        </label>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
          />
        </FormField>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}
